import { CountryCode } from 'libphonenumber-js';

import { Patterns } from '@shared/components/entity/patterns';

/**
 * Country-specific shapes for the national identifiers KOR captures.
 *
 * Two things here are load-bearing and easy to get wrong:
 *
 * 1. `pattern` matches BARE DIGITS. The form model holds digits and the formatter inserts separators on submit
 *    (see `formatSocialId` / `formatBusinessTaxId`), which is the existing house behaviour for US.
 * 2. `maskLength` differs by country. A Canadian SIN masks to its last GROUP (3 chars) where a US SSN masks to
 *    its last 4. Never compare a value against a hardcoded 4 - use `isMaskedIdentifier`.
 */
export type IdentityCountryCode = 'USA' | 'CAN';

export interface IdentityFormat {
  /** Matches the unformatted value the form model holds. */
  pattern: RegExp;
  /** Display mask and placeholder, e.g. `XXX-XX-XXXX`. */
  mask: string;
  /** Prefix the secure input renders in front of the stored mask. */
  securedValuePrefix: string;
  /** Length of the mask the API returns for this country. */
  maskLength: number;
  maxLength: number;
  label: string;
  patternMessage: string;
}

export const SOCIAL_ID_FORMATS: Record<IdentityCountryCode, IdentityFormat> = {
  USA: {
    pattern: Patterns.SOCIAL_SECURITY_NUMBER,
    mask: 'XXX-XX-XXXX',
    securedValuePrefix: 'XXX-XX-',
    maskLength: 4,
    maxLength: 11,
    label: 'Social ID Number (SSN)',
    patternMessage: 'Enter a valid SSN',
  },
  CAN: {
    pattern: Patterns.SOCIAL_INSURANCE_NUMBER,
    mask: 'XXX-XXX-XXX',
    securedValuePrefix: 'XXX-XXX-',
    maskLength: 3,
    maxLength: 11,
    label: 'Social ID Number (SIN)',
    patternMessage: 'Enter a valid SIN',
  },
};

export const BUSINESS_TAX_ID_FORMATS: Record<IdentityCountryCode, IdentityFormat> = {
  USA: {
    pattern: Patterns.BUSINESS_TAX_ID,
    mask: 'XX-XXXXXXX',
    securedValuePrefix: 'XX-XXX',
    maskLength: 4,
    maxLength: 10,
    label: 'Business Tax ID',
    patternMessage: 'Enter a valid tax id',
  },
  CAN: {
    // Canadian Business Numbers carry no separators at all.
    pattern: Patterns.BUSINESS_NUMBER,
    mask: 'XXXXXXXXX',
    securedValuePrefix: 'XXXXX',
    maskLength: 4,
    maxLength: 9,
    label: 'Business Number (BN)',
    patternMessage: 'Enter a valid Business Number',
  },
};

/**
 * Countries KOR captures national identifiers for. Drives the country selects.
 *
 * Note these are iso2 - the select filters its option list by iso2 but emits iso3 (`USA`/`CAN`), which is what the
 * format lookups key off.
 */
export const IDENTITY_COUNTRY_RESTRICTIONS: CountryCode[] = ['US', 'CA'];

export const DEFAULT_IDENTITY_COUNTRY: IdentityCountryCode = 'USA';

/** Accepts either the iso3 value the form selects (`CAN`) or the iso2 form the API sometimes returns (`CA`). */
export const isCanadianIdentityCountry = (countryCode?: string | null): boolean => countryCode === 'CAN' || countryCode === 'CA';

const asIdentityCountry = (countryCode?: string | null): IdentityCountryCode =>
  isCanadianIdentityCountry(countryCode) ? 'CAN' : DEFAULT_IDENTITY_COUNTRY;

export const socialIdFormat = (countryCode?: string | null): IdentityFormat => SOCIAL_ID_FORMATS[asIdentityCountry(countryCode)];

export const businessTaxIdFormat = (countryCode?: string | null): IdentityFormat => BUSINESS_TAX_ID_FORMATS[asIdentityCountry(countryCode)];

/**
 * True when a value is a mask returned by the API rather than a real identifier.
 *
 * Deliberately `length < 9` rather than an exact match against `maskLength`: the shortest real identifier is a
 * Canadian Business Number at exactly 9 characters, while the longest mask is 5 (a US value stored with a stray
 * trailing separator). An exact comparison also silently misreads a 3-char Canadian mask as a real value when the
 * country is unknown, which re-formats it into garbage and posts it back.
 */
export const isMaskedIdentifier = (value?: string | null): boolean => !!value && value.length < 9;

/**
 * Countries the address autocomplete offers.
 *
 * Separate from IDENTITY_COUNTRY_RESTRICTIONS on purpose: which countries we can capture an address for is a
 * different question from which national identifiers we validate, and the two sets will not always match.
 */
export const ADDRESS_COUNTRY_RESTRICTIONS: CountryCode[] = ['US', 'CA'];

/**
 * Countries a restricted phone field accepts.
 *
 * Most customer phone fields pass `countryRestrictions: []` (unrestricted) and need nothing from this. The
 * beneficiary form is the exception - it restricts, so it has to name Canada explicitly or a Canadian beneficial
 * owner's mobile number fails validation even though US and Canada share the NANP country code.
 */
export const PHONE_COUNTRY_RESTRICTIONS: CountryCode[] = ['US', 'CA'];

export const RESTRICTED_PHONE_MESSAGE = 'Enter a US or Canadian phone number';

/** Labels that have to cover both countries. */
export const STATE_PROVINCE_LABEL = 'State/Province';
export const POSTAL_CODE_LABEL = 'Zip/Postal Code';
